import { useAnalytics } from '@ecosystem/newron-client-lib';
import { containers, elements, feedback } from '@ecosystem/newron-design-system';
import { capitalize, isEmpty } from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import ProductsOverviewDoughnutContainer from '@components/containers/products/ProductsOverviewDoughnutContainer';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import ProductKpiTilesContainer from '@components/product-kpi-tiles-container/ProductKpiTilesContainer';
import { FocusArea } from '@components/products-top-5-table/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import ProductsTable from '@components/top-selling-products/ProductsTable';
import { DisplayOptions } from '@components/top-selling-products/products-table-filter/Types';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { ComponentName, initialOverviewKpi, OverviewKpi, SaleType } from '@models/Products';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { getProductsOverview } from '@services/productTab/ProductsOverview.service';
import { IRootState } from '@state/reducers/Reducers';
import { getAppliedFiltersFromPagesConfig, getInitialAppliedCountries } from '@utils/FilterUtils';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import { checkPeriodExist, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import {
  NavigateSourceType,
  PAGE_NAVIGATE_TRACKING_TITLE,
  pageNavigateTrackingFunction,
  PageNavigateTrackingFunctionParam,
} from '@utils/page-analytics/model/PageNavigateTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  CountryFilterPair,
  FactRevenue,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  SubSection,
} from '@utils/pageConfig/Types';
import { constructProductsParam } from '@utils/serviceUtils/RequestFormatter';
import { NoResult } from '@pages/NoResult';
import { RI_PRODUCT_OVERVIEW_VIEW_FULL_PRODUCT_TABLE, RI_PRODUCT_REVENUE_CONCENTRATION_TITLE } from '@pages/Utilities';
import {
  applyTimeFramePickerProductsCallback,
  countriesToQuery,
  getCurrentRetailerClientState,
  isInvalidPG,
  noResultForProductOverviewTab,
  onTabLinkClickHandler,
  ProductsParamArgumentObject,
  ProductType,
} from '../Product';
import * as styles from './Overview.module.scss';
import { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { bindActionCreators } from 'redux';
import { actionCreatorsHitlistsRetailerClient, actionCreatorsProducts } from '@state/Index';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import {
  PAGE_ELEMENT_CATEGORY_CTA,
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  PAGE_ELEMENT_TYPE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { NavigationLinkProps } from '@components/tab-nav/TabNav';
import { WithAlert } from '@components/design';
import { BASE_RETAIL_PATH, RETAIL_ALLOWED_PATH } from '@utils/UrlUtils';
import { getCountryFilterPairsByTimeFrame } from '@utils/CountryUtils';
import ErrorResultMessage from '@pages/ErrorResultMessage';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

interface Props {
  filters: MainFilters;
  timeFrame: Periodicity;
  period: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  setTimeFrame: Dispatch<SetStateAction<Periodicity>>;
  setPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  setComparisonPeriod: Dispatch<SetStateAction<RelativePeriod | BaseTimePeriod>>;
  selectedProductGroup: Option[] | undefined;
  appliedCountries: Option[];
  setSelectedLink: Dispatch<SetStateAction<NavigationLinkProps>>;
  selectedLink: any;
  timeFrameOnChangeHandler: any;
  pgListByCountry: CountryFilterPair[];
  factRevenue: FactRevenue;
  currentProductType: ProductType;
  isMultibannerChanging: boolean;
  defaultFilters: MainFilters[];
  prodLeaderboardFeatureProductFilters: Filters[];
}

const Overview: React.FC<Props> = ({
  filters,
  timeFrame,
  period,
  comparisonPeriod,
  setTimeFrame,
  setPeriod,
  setComparisonPeriod,
  selectedProductGroup,
  appliedCountries,
  setSelectedLink,
  selectedLink,
  timeFrameOnChangeHandler,
  pgListByCountry,
  factRevenue,
  currentProductType,
  isMultibannerChanging,
  defaultFilters,
  prodLeaderboardFeatureProductFilters,
}) => {
  const { ActionType } = feedback;
  const pagesConfig = usePageConfig();

  // Redux
  const dispatch = useDispatch();
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const hitlistsRetailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);
  const sellerInsightsRetailerClientState = useSelector((state: any) => state.sellerInsightsRetailerClient);

  const currentRetailerClientState = getCurrentRetailerClientState(
    currentProductType,
    retailerClientState,
    hitlistsRetailerClientState,
    sellerInsightsRetailerClientState,
  );

  const mainFilterStateProducts = useSelector((state: IRootState) => state.mainFiltersProducts);
  const sideFiltersState: SideFiltersState = useSelector((state: IRootState) => state.sideFilters);

  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [overviewKpi, setOverviewKpi] = useState<OverviewKpi>(initialOverviewKpi);
  const [saleType, setSaleType] = useState<SaleType>(SaleType.revenue);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RI_PRODUCT);

  //End used for indicative performance inline warning alert

  useEffect(() => {
    //Update page viewing
    updateHitlistsRetailerClient({
      ...hitlistsRetailerClientState,
      pageViewing: PageName.RI_PRODUCT,
    });
  }, []);

  // Debounce the effect multiple calls during period change when either period is not available
  useDebouncedEffect(() => {
    // Allows us to reinvoke the data API call once pgListByCoutry is updated
    if (isMultibannerChanging || pgListByCountry.length <= 0) {
      return;
    }

    // Get the country & pg from pagesConfig to avoid situations where appliedFilters is not updated yet
    const countries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);
    const productGroup = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT);

    if (!countries || !productGroup) {
      return;
    }

    loadData(period, countries, productGroup[0]);
  }, [period, comparisonPeriod, pagesConfig.appliedFilters, isMultibannerChanging, pgListByCountry]);

  const setDataStateToDefault: () => void = () => {
    setOverviewKpi(initialOverviewKpi);
    setIsLoading(false);
    setIsApiError(false);
  };

  const loadData = async (responseTimePeriod: BaseTimePeriod, countries: Option[], pg: Option) => {
    const countriesToQueryObject = countriesToQuery(pagesConfig, countries, effectiveProductType);
    const responseTimeFrame = checkPeriodExist(defaultFilters, countriesToQueryObject[0]?.id, timeFrame);

    if (
      isInvalidPG(responseTimeFrame, countries, [pg], pgListByCountry, pagesConfig) ||
      !selectedProductGroup ||
      !responseTimeFrame
    ) {
      setDataStateToDefault();
      return;
    }

    try {
      setIsLoading(true);

      const productsParamObject: ProductsParamArgumentObject = {
        productType: effectiveProductType,
        pageNumber: 0,
        productGroupParam: pg,
        pagesConfig: pagesConfig,
        period: responseTimePeriod,
        mainFilterStateProducts: mainFilterStateProducts,
        retailerClientState: currentRetailerClientState,
        countriesToQuery: countriesToQueryObject,
        timeFrame: responseTimeFrame,
        defaultPageSize: 0,
        selectedSaleType: null,
        sortingKpi: undefined,
        isMarket: FocusArea.market,
        growthType: undefined,
        comparisonPeriod: comparisonPeriod,
      };

      const productsParam = constructProductsParam(productsParamObject);
      if (!productsParam) {
        return;
      }

      const productsOverviewData = await getProductsOverview(productsParam);
      setOverviewKpi(productsOverviewData);
    } catch (err: any) {
      console.error(err);
      saveErrorLog(err, 'Products Overview - Unable to get products overview api response.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: currentRetailerClientState.clientId,
          productGroup: selectedProductGroup,
          period: responseTimePeriod,
          comparisonPeriod: comparisonPeriod,
        },
      });
      //Set back to initial state, to show no result if error occur.
      setDataStateToDefault();
      setApiErrorState(err, setIsApiError);
    } finally {
      // Listen and used to prevent multiple api calling , and set false sequence is important
      pagesConfig.setIsRetailerChanged(false);
      pagesConfig.setIsCountryChanged(false);
      setIsLoading(false);
    }
  };

  const pageElementViewParam = useGeneratePageElementViewParam();
  const overviewPageElementParam = (elementType: ElementType) => () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    componentName: ComponentName.PRODUCT_REVENUE_CONCENTRATION_TOP_5_PRODUCT,
    elementType,
    elementKpi: capitalize(saleType),
    hasData: !isEmpty(overviewKpi.items),
    access: pagesConfig.access,
    subSection: SubSection.PRODUCT_OVERVIEW,
    initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, effectiveProductType),
  });

  const { ref: ProductsTableRevenueConcentrationTop5Component } = useInViewTracking(
    overviewPageElementParam(ElementType.TABLE),
  );
  const { ref: ProductDonutChartComponent } = useInViewTracking(overviewPageElementParam(ElementType.DONUT_CHART));

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, timeFrame);

  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePickerProductsCallback({
      appliedPeriodicity,
      appliedPeriod,
      appliedRelativePeriod,
      pagesConfig,
      mainFilterStateProducts,
      sideFiltersState,
      retailerClientState,
      track,
      setTimeFrame,
      setPeriod,
      setRelativePeriod: setComparisonPeriod,
      updateMainFiltersProducts,
    });
  };

  const handleElementClickTracking = () => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      elementName: 'View full product table',
      access: pagesConfig.access,
      elementCategory: PAGE_ELEMENT_CATEGORY_CTA,
      elementType: PAGE_ELEMENT_TYPE,
    };
    pageElementClickTrackingFunction(params);
  };

  const renderTable = () => {
    const { Card } = containers;
    return isLoading ? (
      <></>
    ) : (
      <div className={styles.Overview__Table}>
        <ProductsTable
          productsLeaderboard={overviewKpi.items}
          selectedDisplayType={DisplayOptions.overview}
          selectedSaleType={saleType}
          factRevenue={factRevenue}
          selectedGrowthType={GrowthTypes.actual}
          setSelectedSaleType={setSaleType}
          isViewTypeChanged={false}
          isTableHeaderSticky={false}
          prodLeaderboardFeatureProductFilters={prodLeaderboardFeatureProductFilters}
          productSpecificationFilters={[]}
        />
        <Card>
          <Card.Content>
            <button
              data-testid="leaderboard-redirect-link"
              className={`flex text-brand hover:text-brand-dark pl-1 cursor-pointer font-bold`}
              onClick={() => {
                track('Link clicked: Leaderboard');
                // Navigate to Leaderboard Tab Amplitude tracking
                navigateToLeaderboard(track, pagesConfig.access);
                onTabLinkClickHandler(selectedLink, navigate, setSelectedLink);
                handleElementClickTracking();
              }}
            >
              {RI_PRODUCT_OVERVIEW_VIEW_FULL_PRODUCT_TABLE}
              <elements.Icon color="brand">
                <elements.Icon.icons.ChevronRight />
              </elements.Icon>
            </button>
          </Card.Content>
        </Card>
      </div>
    );
  };

  const renderDonutAndTableView = () => {
    return renderVisualization({
      isLoading: isLoading,
      fetchedApiData: overviewKpi,
      defaultData: initialOverviewKpi,
      noResultPredicate: () => noResultForProductOverviewTab(overviewKpi),
      jsxToRender: (
        <div className={styles.Overview__Revenue_Concentration} ref={ProductsTableRevenueConcentrationTop5Component}>
          <div className={styles.Overview__Revenue_Concentration_Row} ref={ProductDonutChartComponent}>
            <ProductsOverviewDoughnutContainer
              overviewKpi={overviewKpi}
              isLoading={isLoading}
              currencySymbol={factRevenue.currencySymbol}
            />
            {renderTable()}
          </div>
        </div>
      ),
      noResultJsx: (
        <>
          <div ref={ProductDonutChartComponent}>{NoResult()}</div>
          <div ref={ProductsTableRevenueConcentrationTop5Component}></div>
        </>
      ),
      errorMessageJsx: (
        <>
          <div ref={ProductDonutChartComponent}>{ErrorResultMessage()}</div>
          <div ref={ProductsTableRevenueConcentrationTop5Component}></div>
        </>
      ),
      isError: isApiError,
    });
  };

  const renderOverviewInfo = () => {
    const { Card } = containers;
    return (
      <>
        <ProductKpiTilesContainer
          isLoading={isLoading}
          overviewKpi={overviewKpi}
          subSectionForAmplitudeTracking={SubSection.PRODUCT_OVERVIEW}
        />
        <div className="pt-6">
          <Card>
            <Card.Title title={RI_PRODUCT_REVENUE_CONCENTRATION_TITLE} />
            <Card.Content>
              <div className="mb-2">How concentrated is the segment revenue around critical product lines</div>
              {renderDonutAndTableView()}
            </Card.Content>
          </Card>
        </div>
        <div className="my-4">
          <WithAlert
            title={
              <div>
                <span>
                  As part of ongoing platform updates, the Indicative Performance Trend chart has been removed. You can
                  find new and improved Trend view charts on the
                </span>
                <Link
                  to={`/${BASE_RETAIL_PATH}/${RETAIL_ALLOWED_PATH.EXPLORE_TRENDS}`}
                  className="text-brand hover:text-brand-dark pl-1"
                >
                  Explore page
                </Link>
                <span>.</span>
              </div>
            }
            type="info"
            actionType={ActionType.DEFAULT}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <div className="flex items-end pt-6 pb-4">
        <div className={`${styles.Overview__Controls} grid w-full`}>
          <TimeFramePickerWrapper
            isPeriodOptionsDisabled={false}
            isTimeFramePickerDisabled={isLoading}
            periodFormat={PeriodFormat.LONG}
            currentPeriodicity={timeFrame}
            currentPeriod={period}
            currentComparison={comparisonPeriod}
            periodOptions={filters.timePeriod}
            applyPickerCallback={applyTimeFramePickerCallback}
            isPeriodOptionsDisabledPredicate={(periodicity) =>
              ![Periodicity.YTD, Periodicity.MAT].includes(periodicity)
            }
            isHideComparisonPeriodsPanelPredicate={(periodicity) =>
              [Periodicity.YTD, Periodicity.MAT].includes(periodicity)
            }
            isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
          />
        </div>
      </div>
      <div className="pb-4">
        <FilterSelectionNote
          periodFormat={PeriodFormat.LONG}
          selectedPeriodicity={timeFrame}
          selectedPeriod={period}
          comparisonPeriodType={comparisonPeriod}
          isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
          scopeMetadata={filteredScopeData[0]?.scopeMetadata}
        />
      </div>
      <div>
        <div className="pt-4">
          <FilterChips pgListByCountry={pgListByCountry} selectedTimeFrame={timeFrame} />
        </div>
      </div>
      <div className="pt-6">{renderOverviewInfo()}</div>
    </>
  );
};

// Amplitude tracking for tab navigation from Overview tab to Leaderboard tab
const navigateToLeaderboard = (track: (event: string, properties?: Object) => void, access: Object) => {
  //Start Page Navigate Amplitude Tracking
  const trackingParam: PageNavigateTrackingFunctionParam = {
    eventTitle: PAGE_NAVIGATE_TRACKING_TITLE,
    track: track,
    pageName: PageName.RI_PRODUCT,
    linkName: RI_PRODUCT_OVERVIEW_VIEW_FULL_PRODUCT_TABLE,
    navigationSource: NavigateSourceType.JUMP_POINT,
    access: access,
    subSection: SubSection.PRODUCT_OVERVIEW,
  };

  pageNavigateTrackingFunction(trackingParam);
  //End Page Navigate Amplitude Tracking
};

export default Overview;
